import { useWatch } from "react-hook-form";
import { useGetOne, type RaRecord } from "react-admin";
import { Box, Typography, Card, CardContent, Chip, Stack } from "@mui/material";
import { TemplatePreview } from "@/components/whatsapp/TemplatePreview";
import {
  useTemplateContext,
  useRecipientSelection,
} from "@/containers/whatsapp-broadcasts/context";
import type { BroadcastParameter } from "./utils";
import { resolvePreviewVariables } from "./preview-variables";

type TemplateWithComponents = RaRecord & {
  components?: { type: string }[];
  exampleVariables?: { value: string }[];
};

const BroadcastPreview = () => {
  const templateName = useWatch({ name: "templateName" });
  const miqaatId = useWatch({ name: "miqaatId" }) as string | undefined;
  const eventId = useWatch({ name: "eventId" }) as string | undefined;
  const parameters = useWatch({ name: "parameters" }) as
    | Record<string, BroadcastParameter>
    | undefined;
  const recipientCsvData = useWatch({ name: "recipientCsvData" }) as
    | Record<string, Record<string, string>>
    | null
    | undefined;

  const { recipientDetails } = useRecipientSelection();
  const { data: selectedTemplate, isLoading, error } = useTemplateContext();

  const { data: miqaatRecord } = useGetOne(
    "miqaats",
    { id: miqaatId ?? "" },
    { enabled: Boolean(miqaatId?.trim()) }
  );

  const { data: eventRecord } = useGetOne(
    "events",
    { id: eventId ?? "" },
    { enabled: Boolean(eventId?.trim()) }
  );

  if (!templateName) {
    return (
      <Box sx={{ width: "100%", maxWidth: 380, mx: "auto" }}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="body2" color="text.secondary" align="center">
              Select a template to see preview
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", maxWidth: 380, mx: "auto" }}>
        <Typography color="text.secondary">Loading template...</Typography>
      </Box>
    );
  }

  if (error || !selectedTemplate) {
    return (
      <Box sx={{ width: "100%", maxWidth: 380, mx: "auto" }}>
        <Typography color={error ? "error" : "text.secondary"}>
          {error
            ? `Error loading template: ${(error as Error).message}`
            : `Template not found${templateName ? `: ${templateName}` : ""}`}
        </Typography>
      </Box>
    );
  }

  const firstRecipient = recipientDetails?.[0] || null;
  const firstCsvRow =
    recipientCsvData && typeof recipientCsvData === "object"
      ? Object.values(recipientCsvData)[0] || null
      : null;

  const previewResult = resolvePreviewVariables({
    templateName,
    parameters,
    firstRecipient,
    firstCsvRow,
    miqaatRecord: miqaatRecord as Record<string, unknown> | null | undefined,
    eventRecord: eventRecord as Record<string, unknown> | null | undefined,
  });

  const previewData: TemplateWithComponents = {
    ...selectedTemplate,
    components: selectedTemplate.components || [],
  };

  if (previewResult.variables.length > 0 && previewData.components) {
    const bodyComponent = previewData.components.find((c) => c.type === "BODY");
    if (bodyComponent) {
      previewData.exampleVariables = previewResult.variables;
    }
  }

  return (
    <Box
      sx={{
        position: { xs: "static", md: "sticky" },
        top: 20,
        width: "100%",
        maxWidth: 380,
        mx: "auto",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          mb: 1.5,
          width: "100%",
          minHeight: 28,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            fontSize: "0.875rem",
            lineHeight: 1.2,
            color: "text.primary",
          }}
        >
          Message Preview
        </Typography>
        {previewResult.isUsingActualData ? (
          <Chip
            label={
              previewResult.rowSummaryLabel
                ? previewResult.rowSummaryLabel.startsWith("Row 1:")
                  ? `Row 1: ${previewResult.rowSummaryLabel.replace(/^Row 1:\s*/, "")}`
                  : previewResult.rowSummaryLabel
                : "Actual data"
            }
            size="small"
            color="primary"
            variant="outlined"
            sx={{ maxWidth: 220 }}
          />
        ) : (
          <Chip label="Sample values" size="small" variant="outlined" />
        )}
      </Stack>

      <TemplatePreview formData={previewData} />
    </Box>
  );
};

export default BroadcastPreview;
